import type { JSX, MouseEvent } from "react";

import { MoonIcon, SunIcon } from "@/components/icons";
import { Tooltip } from "@/components/Tooltip";
import { useTheme } from "@/lib/theme";

import styles from "./ThemeToggle.module.css";

/**
 * Hell/Dunkel in the masthead (decision Denis 24.10.2026, design
 * 2026-10-24 §5.1): one toggle named „Dunkelmodus", pressed while the page is
 * dark. A sun and a moon side by side, and a knob that slides under the one
 * that is on — the state shows by position as well as by the brighter
 * symbol, not by colour alone.
 *
 * The click hands `lib/theme.ts` the middle of the switch, where the reveal
 * opens as a circle (§7); a key press clicks too, and a keyboard's click
 * lands on the same box. Where no view transition may run, or storage is
 * blocked, `switchTheme` changes the mode at once and for the session — this
 * component has nothing to decide there.
 *
 * Like every glyph without a word (BW-2) it carries a tooltip, the same words
 * as its name; there is no `title`, which would be a second tooltip.
 */
export function ThemeToggle(): JSX.Element {
  const { theme, setTheme } = useTheme();
  const dark = theme === "dark";

  function toggle(event: MouseEvent<HTMLButtonElement>) {
    const box = event.currentTarget.getBoundingClientRect();
    setTheme(dark ? "light" : "dark", { x: box.left + box.width / 2, y: box.top + box.height / 2 });
  }

  return (
    <Tooltip text="Dunkelmodus">
      <button type="button" className={styles.toggle} aria-pressed={dark} aria-label="Dunkelmodus" onClick={toggle}>
        <span className={styles.knob} aria-hidden="true" />
        <SunIcon size={14} className={`${styles.symbol} ${styles.sun}`} />
        <MoonIcon size={14} className={`${styles.symbol} ${styles.moon}`} />
      </button>
    </Tooltip>
  );
}
